import { AdvicePlantResponse, AdviceSeedResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/advice/advice-today.response';
import { StepStage } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/step-stage.enum';
import { GrowingStepSource } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/growing-step-source.enum';
import { GreenLightGesture, GreenLightReason, GreenLightStatus } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/green-light.enum';
import { LocationType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/location-type.enum';
import routesConstants from '../../../@commons/constants/routes.constants';
import { formatVwDate } from '../../../@commons/pipes/vw-date.pipe';
import { nounPhrase } from '../../../@commons/utils/noun-phrase.util';
import { lastFileUrl } from '../../../garden/@components/last-file-url.util';
import { DayCandidate, DayMessageDef, FROST_C, WIND_KMH, forecast, fromVerdicts, weekday, when } from './day-message.catalogue';
import { DayContext, DayPlant, DayText, localDay, plantWords } from './day-message.model';

/*
 * Les règles du catalogue qui partent des plantes et des sachets du jardin
 * (`advices/today` : `plants`, `seeds`), avec la prévision des jours qui
 * viennent. Seuils et textes de `catalogue-mot-du-jour.csv` (climat océanique
 * tempéré) ; une espèce se reconnaît à son nom scientifique, stable d'un
 * environnement à l'autre.
 */

const OUTDOOR: string[] = [LocationType.GARDEN, LocationType.PLANTER_BOX, LocationType.TERRACE, LocationType.BALCONY];
const POTS: string[] = [LocationType.PLANTER_BOX, LocationType.TERRACE, LocationType.BALCONY];
const GROWING = [StepStage.GROWTH, StepStage.FLOWERING, StepStage.HARVEST];
const DAY_MS = 86_400_000;
/** Une nuit sous ce seuil gèle les racines d'une plante en pot. */
const POT_FROST_C = -5;
/** Une souche d'artichaut gèle à ce froid : le geste devient de la sécurité. */
const ARTICHOKE_LOSS_C = -5;
/** Les plants élevés à l'abri sortent quand les nuits passent ce seuil. */
const MILD_NIGHT_C = 10;
const WINTER = [11, 12, 1, 2];

/** Espèces nommées par les règles : le début du nom scientifique. */
const SPECIES = {
  potato: ['Solanum tuberosum'],
  tomato: ['Solanum lycopersicum'],
  strawberry: ['Fragaria'],
  lettuce: ['Lactuca'],
  carrot: ['Daucus'],
  broadBean: ['Vicia faba'],
  pea: ['Pisum'],
  bean: ['Phaseolus'],
  maize: ['Zea'],
  courgette: ['Cucurbita pepo'],
  squash: ['Cucurbita'],
  leek: ['Allium porrum', 'Allium ampeloprasum'],
  garlic: ['Allium sativum'],
  onion: ['Allium cepa'],
  chive: ['Allium schoenoprasum'],
  brussels: ['Brassica oleracea var. gemmifera', 'Brassica oleracea gemmifera'],
  cabbage: ['Brassica oleracea'],
  parsnip: ['Pastinaca'],
  salsify: ['Tragopogon', 'Scorzonera'],
  beet: ['Beta vulgaris'],
  celeriac: ['Apium graveolens'],
  lambsLettuce: ['Valerianella'],
  spinach: ['Spinacia'],
  radish: ['Raphanus'],
  rocket: ['Eruca'],
  rhubarb: ['Rheum'],
  artichoke: ['Cynara'],
  vine: ['Vitis'],
};
/** Les plantes hautes, à tuteurer. */
const TALL = [...SPECIES.tomato, ...SPECIES.bean, ...SPECIES.pea, ...SPECIES.broadBean, ...SPECIES.maize];
/** Ce qui passe l'hiver dehors sans souci. */
const HARDY = [...SPECIES.leek, ...SPECIES.cabbage, ...SPECIES.lambsLettuce, ...SPECIES.spinach, ...SPECIES.parsnip, ...SPECIES.celeriac];
/** Ce que le gel rend plus doux. */
const SWEETER_AFTER_FROST = [...SPECIES.cabbage, ...SPECIES.parsnip, ...SPECIES.leek];
/** Les sachets « pour la suite » : leur semis ouvre au printemps. */
const SPRING_MONTHS = [2, 3, 4, 5];
/** Ce qui se garde en terre plutôt qu'au frigo. */
const KEEPS_IN_GROUND = [...SPECIES.carrot, ...SPECIES.parsnip, ...SPECIES.beet, ...SPECIES.celeriac, ...SPECIES.leek];
/** Un petit rang toutes les trois semaines. */
const SUCCESSION = [...SPECIES.radish, ...SPECIES.lettuce, ...SPECIES.bean, ...SPECIES.spinach, ...SPECIES.rocket];
/** Des graines qui ne se gardent pas : un an pour le panais, deux pour l'oignon et le poireau. */
const SHORT_LIVED_SEEDS = [...SPECIES.parsnip, ...SPECIES.onion, ...SPECIES.leek, ...SPECIES.chive, ...SPECIES.salsify];
/** Semis échelonné : le dernier rang a au moins trois semaines. */
const SUCCESSION_DAYS = 21;

export function isSpecies(item: { species: string | null }, names: string[]): boolean {
  const species = (item.species ?? '').toLowerCase();
  return names.some((name) => species.startsWith(name.toLowerCase()));
}

function gardenPlants(ctx: DayContext): AdvicePlantResponse[] {
  return ctx.advice?.plants ?? [];
}

function seeds(ctx: DayContext): AdviceSeedResponse[] {
  return ctx.advice?.seeds ?? [];
}

function outdoor(p: AdvicePlantResponse): boolean {
  return OUTDOOR.includes(p.effectiveLocationType);
}

function inPot(p: AdvicePlantResponse): boolean {
  return POTS.includes(p.effectiveLocationType);
}

function resting(p: AdvicePlantResponse): boolean {
  return p.lifeState === 'RESTING' || p.stage === StepStage.REST;
}

function daysSince(ctx: DayContext, date: string | null): number | null {
  return date ? Math.round((new Date(localDay(ctx.now)).getTime() - new Date(date).getTime()) / DAY_MS) : null;
}

/** Jours finis sans pluie depuis la dernière pluie relevée (21 au plus, la fenêtre du relevé). */
function dryDays(ctx: DayContext): number {
  return daysSince(ctx, ctx.advice?.lastRain?.date ?? null) ?? 21;
}

function rainAhead(ctx: DayContext, count: number): number {
  return forecast(ctx).slice(0, count).reduce((sum, d) => sum + (d.precipitation ?? 0), 0);
}

/** Une plante du jardin dans un message : son nom, la photo de sa fiche, sa zone, sa grammaire. */
function gardenPlant(ctx: DayContext, p: AdvicePlantResponse): DayPlant {
  const plant = p.plantUid ? ctx.plants?.find((x) => x.uid === p.plantUid) : undefined;
  return {
    uid: p.plantUid,
    name: p.cultivationName,
    cultivationUid: p.cultivationUid,
    imageUrl: (plant && lastFileUrl(plant.cultivationVariant, '')) || null,
    location: p.zone?.name ?? null,
    plantedAt: p.plantedDate,
    plant,
    grammar: p.grammar,
  };
}

function seedPlant(s: AdviceSeedResponse): DayPlant {
  return { uid: null, name: s.cultivationName, imageUrl: null, location: null, grammar: s.grammar };
}

/** La variété et le lieu de la plante en tête de raison (« Poireau de Liège, Planche 1. »), ce qu'on en connaît. */
function where(p: AdvicePlantResponse): DayText[] {
  const location = p.zone?.name;
  if (p.variantName && location) return [{ key: 'home.day.reason.variety-location', params: { variety: p.variantName, location } }];
  if (p.variantName) return [{ key: 'home.day.reason.variety', params: { variety: p.variantName } }];
  return location ? [{ key: 'home.day.reason.location', params: { location } }] : [];
}

/**
 * Les plantes du jardin qui répondent à la règle, une par candidat : la règle
 * groupée les réunit en un message. Un stade que la personne n'a pas déclaré
 * (degrés-jours, ou source inconnue, comme le compte le serveur) rend le
 * message « estimé » ; seule une règle qui ne lit pas le stade passe
 * `estimated: false`.
 */
function plantsWhere(
  ctx: DayContext,
  code: string,
  test: (p: AdvicePlantResponse) => boolean,
  extra: Omit<DayCandidate, 'code' | 'plants'> = {},
): DayCandidate[] {
  return gardenPlants(ctx)
    .filter(test)
    .map((p) => ({ code, plants: [gardenPlant(ctx, p)], reason: where(p), estimated: p.stepSource !== GrowingStepSource.DECLARED, ...extra }));
}

/** Une fenêtre de semis ou de plantation ouverte aujourd'hui ; elle peut passer d'une année à l'autre. */
export function periodOpen(s: AdviceSeedResponse, type: 'SEEDLING' | 'PLANTING', now: Date): boolean {
  const md = (now.getMonth() + 1) * 100 + now.getDate();
  return s.periods.some((p) => {
    if (p.type !== type) return false;
    const start = p.startMonth * 100 + p.startDay;
    const end = p.endMonth * 100 + p.endDay;
    return start <= end ? md >= start && md <= end : md >= start || md <= end;
  });
}

/** La prochaine ouverture d'une fenêtre de semis, après aujourd'hui. */
function nextSowing(s: AdviceSeedResponse, now: Date): Date | null {
  const dates = s.periods
    .filter((p) => p.type === 'SEEDLING')
    .map((p) => {
      const date = new Date(now.getFullYear(), p.startMonth - 1, p.startDay);
      if (date <= now) date.setFullYear(date.getFullYear() + 1);
      return date;
    });
  return dates.sort((a, b) => a.getTime() - b.getTime())[0] ?? null;
}

/** Les sachets qui attendent le printemps : aucune fenêtre ouverte, la prochaine de février à mai. */
function springSeeds(ctx: DayContext): { seed: AdviceSeedResponse; opens: Date }[] {
  return seeds(ctx)
    .filter((s) => !periodOpen(s, 'SEEDLING', ctx.now))
    .map((s) => ({ seed: s, opens: nextSowing(s, ctx.now) }))
    .filter((x): x is { seed: AdviceSeedResponse; opens: Date } => !!x.opens && SPRING_MONTHS.includes(x.opens.getMonth() + 1))
    .sort((a, b) => a.opens.getTime() - b.opens.getTime());
}

/**
 * La question d'exemple à Willy, un jour calme : elle part du sachet qui se
 * sème le plus tôt (« Je sème mes fèves maintenant ou en février ? »). Sans
 * nom relu, la question nomme la culture sans accord.
 */
export function willyQuestion(ctx: DayContext): DayText | null {
  const first = springSeeds(ctx)[0];
  if (!first) return null;
  const month = formatVwDate(first.opens, 'month-name', ctx.lang);
  const mine = nounPhrase(first.seed.grammar, ctx.lang, { owner: 'me' });
  return mine
    ? { key: 'home.day.willy.question', params: { plants: mine, month } }
    : { key: 'home.day.willy.question-plain', params: { names: plantWords([seedPlant(first.seed)], ctx.lang).params['names'], month } };
}

/** Une zone du jardin sans plante vivante. */
function freeZone(ctx: DayContext): { uid: string; name: string } | null {
  const used = new Set(gardenPlants(ctx).map((p) => p.zone?.uid));
  return (ctx.zones ?? []).find((z) => !used.has(z.uid)) ?? null;
}

/** La nuit qui vient : la plus basse de demain matin. Celle d'aujourd'hui est passée. */
function tonight(ctx: DayContext) {
  return forecast(ctx)[1] ?? null;
}

/** Les plantes gélives dehors, sous la menace du gel de cette nuit. */
function frostTonight(ctx: DayContext): number | null {
  const night = tonight(ctx);
  return night?.tempMin != null && night.tempMin <= FROST_C ? Math.round(night.tempMin) : null;
}

const potatoUp = (p: AdvicePlantResponse) => isSpecies(p, SPECIES.potato) && outdoor(p) && [StepStage.EMERGENCE, StepStage.GROWTH].includes(p.stage);

/**
 * Les règles, dans l'ordre du catalogue. `rank` ordonne la sécurité : le gel
 * de cette nuit, puis le gel au sol, le vent et les pots.
 */
export const PLANT_RULES: DayMessageDef[] = [
  // Sécurité : une perte possible cette nuit ou dans les jours qui viennent.
  {
    id: 'GEL_POMMES_DE_TERRE', family: 'securite', priority: 1, rank: 1, tone: 'frost', icon: 'snowflake', weather: true, announces: 'FROST', group: true,
    months: [4, 5, 6], like: 'FROST_TONIGHT', overrides: ['FROST_TONIGHT', 'GEL_AU_SOL'], defaultReason: true,
    button: { key: 'covered', action: 'DONE' },
    collect: (ctx) => {
      const t = frostTonight(ctx);
      if (t == null) return [];
      // La levée estimée se dit à part, avec sa pastille ; la consigne suit (« Un voile ou de la paille suffit. »).
      return plantsWhere(ctx, 'GEL_POMMES_DE_TERRE', potatoUp, { params: { t }, eventDate: tonight(ctx)!.date }).map((c) =>
        c.estimated ? { ...c, reason: [...(c.reason ?? []), { key: 'home.day.msg.GEL_POMMES_DE_TERRE.stage', estimated: true }] } : c,
      );
    },
  },
  {
    id: 'GEL_FRAISIERS_FLEURS', family: 'securite', priority: 1, rank: 1, tone: 'frost', icon: 'snowflake', weather: true, announces: 'FROST', group: true,
    months: [3, 4, 5, 6], like: 'FROST_TONIGHT', overrides: ['FROST_TONIGHT', 'GEL_AU_SOL'], defaultReason: true,
    button: { key: 'covered', action: 'DONE' },
    collect: (ctx) => {
      const t = frostTonight(ctx);
      if (t == null) return [];
      return plantsWhere(ctx, 'GEL_FRAISIERS_FLEURS', (p) => isSpecies(p, SPECIES.strawberry) && outdoor(p) && p.stage === StepStage.FLOWERING, {
        params: { t }, eventDate: tonight(ctx)!.date,
      });
    },
  },
  {
    // L'automne : ce qui reste sur les pieds gélifs se cueille avant le gel.
    id: 'GEL_DERNIERS_FRUITS', family: 'securite', priority: 1, rank: 2, tone: 'frost', icon: 'snowflake', weather: true, announces: 'FROST', group: true,
    months: [9, 10, 11], like: 'FROST_TONIGHT', overrides: ['FROST_TONIGHT', 'DERNIERES_RECOLTES', 'RECOLTE_PRETE'], defaultReason: true,
    button: { key: 'picked', action: 'PICKED' },
    collect: (ctx) => {
      const t = frostTonight(ctx);
      if (t == null) return [];
      return plantsWhere(ctx, 'GEL_DERNIERS_FRUITS', (p) => p.frostSensitive && outdoor(p) && p.stage === StepStage.HARVEST, {
        params: { t }, eventDate: tonight(ctx)!.date,
      });
    },
  },
  {
    // Nuit claire et sans vent : la terre se refroidit plus que l'air, les jeunes plants y sont les plus exposés.
    id: 'GEL_AU_SOL', family: 'securite', priority: 1, rank: 3, tone: 'frost', icon: 'snowflake', weather: true, announces: 'FROST', group: true,
    months: [3, 4, 5, 6, 9, 10, 11], estimated: true, defaultReason: true,
    button: { key: 'covered', action: 'DONE' },
    collect: (ctx) => {
      const night = tonight(ctx);
      if (!night?.groundFrost || night.tempMin == null) return [];
      // Le signal dit ce qui gèle, avec la minimale de la nuit que montre la bande : l'air, ou seulement le sol.
      return plantsWhere(ctx, 'GEL_AU_SOL', (p) => (p.isYoung || p.frostSensitive) && outdoor(p) && !resting(p), {
        params: { t: Math.round(night.tempMin) }, signalVariant: night.tempMin <= FROST_C ? 'air' : 'ground', eventDate: night.date, estimated: true,
      });
    },
  },
  {
    id: 'VENT_FORT_TUTEURS', family: 'securite', priority: 1, rank: 4, tone: 'wind', icon: 'wind', weather: true, announces: 'WIND', group: true,
    months: [5, 6, 7, 8, 9, 10], like: 'STRONG_WIND', overrides: ['STRONG_WIND'], defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) => {
      const days = forecast(ctx);
      const index = days.findIndex((d, i) => i <= 2 && (d.windSpeed ?? 0) >= WIND_KMH);
      if (index < 0) return [];
      const day = days[index];
      return plantsWhere(ctx, 'VENT_FORT_TUTEURS', (p) => isSpecies(p, TALL) && outdoor(p) && GROWING.includes(p.stage), {
        params: { n: Math.round(day.windSpeed!), day: weekday(ctx, day.date) }, signalVariant: when(index), eventDate: day.date,
      });
    },
  },
  {
    // En pot, les racines gèlent bien avant celles du jardin.
    id: 'GEL_POTS', family: 'securite', priority: 1, rank: 5, tone: 'frost', icon: 'snowflake', weather: true, announces: 'FROST', group: true,
    months: [11, 12, 1, 2, 3], like: 'FROST_LATER', overrides: ['FROST_TONIGHT', 'FROST_LATER'], defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) => {
      const days = forecast(ctx);
      const index = days.findIndex((d, i) => i >= 1 && d.tempMin != null && d.tempMin <= POT_FROST_C);
      if (index < 0) return [];
      return plantsWhere(ctx, 'GEL_POTS', (p) => inPot(p), {
        params: { t: Math.round(days[index].tempMin!), day: weekday(ctx, days[index - 1].date) }, eventDate: days[index].date, estimated: false,
      });
    },
  },
  {
    // Une nuit sous -5 °C peut geler la souche : le geste passe en sécurité.
    id: 'PROTEGER_ARTICHAUTS', family: 'entretien', priority: 5, tone: 'frost', icon: 'snowflake', weather: true, group: true,
    months: [11, 12], defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) => {
      const coldest = Math.min(...forecast(ctx).slice(1).map((d) => d.tempMin ?? Infinity));
      const loss = coldest <= ARTICHOKE_LOSS_C;
      return plantsWhere(ctx, 'PROTEGER_ARTICHAUTS', (p) => isSpecies(p, SPECIES.artichoke) && outdoor(p), {
        estimated: false, ...(loss ? { priority: 1, family: 'securite' as const } : {}),
      });
    },
  },
  {
    id: 'GARDER_A_L_ABRI', family: 'meteo', priority: 3, tone: 'frost', icon: 'snowflake', weather: true, group: true,
    months: [4, 5], defaultReason: true,
    collect: (ctx) => {
      const cold = forecast(ctx).find((d, i) => i >= 1 && d.tempMin != null && d.tempMin < MILD_NIGHT_C);
      if (!cold) return [];
      return plantsWhere(ctx, 'GARDER_A_L_ABRI', (p) => p.effectiveLocationType === LocationType.INDOOR && p.frostSensitive && !resting(p), {
        params: { t: Math.round(cold.tempMin!), day: weekday(ctx, cold.date) }, eventDate: cold.date,
      });
    },
  },

  // La chaleur et l'eau.
  {
    id: 'SERRE_AERER', family: 'meteo', priority: 2, tone: 'water', icon: 'sun', weather: true, group: true,
    months: [3, 4, 5, 6, 7, 8, 9, 10], defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) => {
      const today = forecast(ctx)[0];
      // 1000 et 1003 : ciel dégagé ou peu nuageux (codes WeatherAPI).
      if (!today || (today.tempMax ?? -Infinity) < 20 || ![1000, 1003].includes(today.conditionCode ?? 0)) return [];
      return plantsWhere(ctx, 'SERRE_AERER', (p) => p.effectiveLocationType === LocationType.GREENHOUSE && !resting(p), {
        params: { t: Math.round(today.tempMax!) }, estimated: false,
      });
    },
  },
  {
    id: 'POTS_CHALEUR', family: 'eau', priority: 2, tone: 'water', icon: 'sun', weather: true, announces: 'HOT', group: true,
    months: [5, 6, 7, 8, 9], like: 'HEATWAVE', overrides: ['HEATWAVE', 'DRY_SPELL'], defaultReason: true,
    button: { key: 'watered', action: 'DONE' },
    collect: (ctx) => {
      const today = forecast(ctx)[0];
      if (!today || (today.tempMax ?? -Infinity) < 25 || (today.precipitation ?? 0) >= 5) return [];
      return plantsWhere(ctx, 'POTS_CHALEUR', (p) => inPot(p) && !resting(p), {
        params: { t: Math.round(today.tempMax!), day: weekday(ctx, today.date) }, signalVariant: 'today', estimated: false,
      });
    },
  },
  {
    id: 'SEC_JEUNES_PLANTS', family: 'eau', priority: 2, tone: 'water', icon: 'droplet', weather: true, announces: 'DRY', group: true,
    months: [4, 5, 6, 7, 8, 9], like: 'DRY_SPELL', overrides: ['DRY_SPELL'], defaultReason: true,
    button: { key: 'watered', action: 'DONE' },
    collect: (ctx) => {
      const dry = dryDays(ctx);
      if (dry < 5 || rainAhead(ctx, 3) >= 2) return [];
      return plantsWhere(ctx, 'SEC_JEUNES_PLANTS', (p) => p.isYoung && outdoor(p) && !resting(p), { params: { n: dry }, signalVariant: 'since' });
    },
  },
  {
    // Un arrosage irrégulier donne des fruits au cul noir.
    id: 'TOMATES_ARROSAGE_REGULIER', family: 'eau', priority: 2, tone: 'water', icon: 'droplet', weather: true, announces: 'DRY', group: true,
    months: [7, 8], like: 'DRY_SPELL', overrides: ['DRY_SPELL'], defaultReason: true,
    button: { key: 'watered', action: 'DONE' },
    collect: (ctx) => {
      const dry = dryDays(ctx);
      if (dry < 4 || !forecast(ctx).slice(0, 2).some((d) => (d.tempMax ?? -Infinity) >= 25)) return [];
      return plantsWhere(ctx, 'TOMATES_ARROSAGE_REGULIER', (p) => isSpecies(p, SPECIES.tomato) && outdoor(p) && [StepStage.FLOWERING, StepStage.HARVEST].includes(p.stage), {
        params: { n: dry }, signalVariant: 'since',
      });
    },
  },
  {
    // La soif d'hier se clôt : la pluie arrive.
    id: 'PAS_BESOIN_ARROSER', family: 'eau', priority: 2, tone: 'water', icon: 'droplet', weather: true, announces: 'RAIN',
    months: [4, 5, 6, 7, 8, 9], defaultReason: true,
    collect: (ctx) => {
      const thirsty = (ctx.recentCodes ?? []).some((c) => ['SEC_JEUNES_PLANTS', 'DRY_SPELL', 'HEATWAVE', 'TOMATES_ARROSAGE_REGULIER', 'POTS_CHALEUR'].includes(c));
      const mm = rainAhead(ctx, 2);
      if (!thirsty || mm < 10 || !gardenPlants(ctx).some(outdoor)) return [];
      return [{ code: 'PAS_BESOIN_ARROSER', plants: [], params: { mm: Math.round(mm) } }];
    },
  },
  {
    id: 'SALADES_CHALEUR', family: 'recolte', priority: 4, tone: 'harvest', icon: 'sun', weather: true, announces: 'HOT', group: true,
    months: [6, 7, 8], like: 'HEATWAVE', overrides: ['RECOLTE_PRETE'], defaultReason: true,
    button: { key: 'picked', action: 'PICKED' },
    collect: (ctx) => {
      const hot = forecast(ctx).slice(0, 3);
      if (hot.length < 3 || !hot.every((d) => (d.tempMax ?? -Infinity) >= 28)) return [];
      return plantsWhere(ctx, 'SALADES_CHALEUR', (p) => isSpecies(p, SPECIES.lettuce) && outdoor(p) && p.stage === StepStage.HARVEST, {
        params: { t: Math.round(hot[0].tempMax!), day: weekday(ctx, hot[0].date) }, signalVariant: 'today',
      });
    },
  },
  {
    id: 'TOMATES_CHALEUR_FLEURS', family: 'croissance', priority: 6, tone: 'calm', icon: 'sun', weather: true, announces: 'HOT', group: true,
    months: [7, 8], like: 'HEATWAVE', defaultReason: true,
    collect: (ctx) => {
      const days = forecast(ctx);
      const index = days.findIndex((d) => (d.tempMax ?? -Infinity) >= 32);
      if (days.filter((d) => (d.tempMax ?? -Infinity) >= 32).length < 2) return [];
      return plantsWhere(ctx, 'TOMATES_CHALEUR_FLEURS', (p) => isSpecies(p, SPECIES.tomato) && p.stage === StepStage.FLOWERING, {
        params: { t: Math.round(days[index].tempMax!), day: weekday(ctx, days[index].date) }, signalVariant: when(index),
      });
    },
  },

  // Le sol, les semis et les plantations.
  {
    id: 'TERRE_RESSUYEE', family: 'sol', priority: 3, tone: 'go', icon: 'sprout', group: true, estimated: true,
    months: [3, 4, 5], defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) => {
      const rain = ctx.advice?.lastRain;
      const zone = freeZone(ctx);
      const dry = dryDays(ctx);
      if (!rain || rain.precipitation < 10 || dry < 2 || dry > 4 || !zone) return [];
      return fromVerdicts(ctx, 'TERRE_RESSUYEE', (v) => v.inSeeds && v.status === GreenLightStatus.GO).map((c) => ({ ...c, params: { n: dry, zone: zone.name } }));
    },
  },
  {
    id: 'TERRE_FROIDE', family: 'sol', priority: 6, tone: 'calm', icon: 'snowflake', group: true, estimated: true,
    months: [4, 5, 6], defaultReason: true,
    collect: (ctx) => fromVerdicts(ctx, 'TERRE_FROIDE', (v) => v.inSeeds && v.reason === GreenLightReason.COLD_SOIL),
  },
  {
    id: 'ENDURCIR_PLANTS', family: 'semis_stock', priority: 3, tone: 'go', icon: 'sun', group: true, estimated: true,
    months: [4, 5], defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) => {
      const soon = new Set(ctx.greenLights.filter((v) => v.status === GreenLightStatus.SOON && v.gesture === GreenLightGesture.PLANT_OUT).map((v) => v.cultivation?.uid));
      return plantsWhere(ctx, 'ENDURCIR_PLANTS', (p) => p.effectiveLocationType === LocationType.INDOOR && p.frostSensitive && soon.has(p.cultivationUid) && !resting(p));
    },
  },
  {
    id: 'SEMIS_ECHELONNE', family: 'semis_stock', priority: 5, tone: 'go', icon: 'sprout', group: true,
    months: [4, 5, 6, 7, 8], defaultReason: true,
    button: { key: 'sown', action: 'DONE' },
    collect: (ctx) =>
      seeds(ctx)
        .filter((s) => isSpecies(s, SUCCESSION) && periodOpen(s, 'SEEDLING', ctx.now))
        .flatMap((s) => {
          const ages = gardenPlants(ctx).filter((p) => p.cultivationUid === s.cultivationUid).map((p) => daysSince(ctx, p.plantedDate) ?? Infinity);
          const last = Math.min(...ages);
          return ages.length && last >= SUCCESSION_DAYS && last !== Infinity ? [{ code: 'SEMIS_ECHELONNE', plants: [seedPlant(s)], params: { n: last } }] : [];
        }),
  },
  {
    id: 'REPIQUER_POIREAUX', family: 'semis_stock', priority: 3, tone: 'go', icon: 'sprout', group: true, estimated: true,
    months: [5, 6, 7], like: 'FEU_VERT_PLANTER', defaultReason: true,
    button: { key: 'planted', action: 'DONE' },
    collect: (ctx) => plantsWhere(ctx, 'REPIQUER_POIREAUX', (p) => isSpecies(p, SPECIES.leek) && p.nextStepStage === StepStage.PLANTING && !resting(p)),
  },
  {
    id: 'PLANTER_AIL', family: 'semis_stock', priority: 3, tone: 'go', icon: 'sprout', number: 'singular',
    months: [10, 11], like: 'FEU_VERT_PLANTER', defaultReason: true,
    button: { key: 'planted', action: 'DONE' },
    collect: (ctx) =>
      seeds(ctx)
        .filter((s) => isSpecies(s, SPECIES.garlic) && periodOpen(s, 'PLANTING', ctx.now))
        .slice(0, 1)
        .map((s) => ({ code: 'PLANTER_AIL', plants: [seedPlant(s)] })),
  },
  {
    id: 'PREGERMER', family: 'hors_saison', priority: 5, tone: 'go', icon: 'sprout',
    months: [2, 3], defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) =>
      seeds(ctx)
        .filter((s) => isSpecies(s, SPECIES.potato))
        .slice(0, 1)
        .map((s) => ({ code: 'PREGERMER', plants: [seedPlant(s)] })),
  },

  // Les récoltes : un geste par espèce, à la place de « C'est le moment de goûter ».
  {
    id: 'COURGETTES_SOUVENT', family: 'recolte', priority: 4, tone: 'harvest', icon: 'basket', group: true,
    months: [7, 8, 9], like: 'RECOLTE_PRETE', overrides: ['RECOLTE_PRETE'], defaultReason: true,
    button: { key: 'picked', action: 'PICKED' },
    collect: (ctx) => plantsWhere(ctx, 'COURGETTES_SOUVENT', (p) => isSpecies(p, SPECIES.courgette) && p.stage === StepStage.HARVEST),
  },
  {
    id: 'HARICOTS_SOUVENT', family: 'recolte', priority: 4, tone: 'harvest', icon: 'basket', group: true,
    months: [7, 8, 9], like: 'RECOLTE_PRETE', overrides: ['RECOLTE_PRETE'], defaultReason: true,
    button: { key: 'picked', action: 'PICKED' },
    collect: (ctx) => plantsWhere(ctx, 'HARICOTS_SOUVENT', (p) => isSpecies(p, SPECIES.bean) && p.stage === StepStage.HARVEST),
  },
  {
    id: 'POMMES_DE_TERRE_ARRACHER', family: 'recolte', priority: 4, tone: 'harvest', icon: 'basket', weather: true, group: true, estimated: true,
    months: [7, 8, 9, 10], like: 'RECOLTE_PRETE', overrides: ['RECOLTE_PRETE'], defaultReason: true,
    button: { key: 'picked', action: 'PICKED' },
    collect: (ctx) => (rainAhead(ctx, 2) >= 1 ? [] : plantsWhere(ctx, 'POMMES_DE_TERRE_ARRACHER', (p) => isSpecies(p, SPECIES.potato) && p.stage === StepStage.HARVEST)),
  },
  {
    id: 'AIL_OIGNONS_ARRACHER', family: 'recolte', priority: 4, tone: 'harvest', icon: 'basket', weather: true, group: true, estimated: true,
    months: [6, 7, 8], like: 'RECOLTE_PRETE', overrides: ['RECOLTE_PRETE'], defaultReason: true,
    button: { key: 'picked', action: 'PICKED' },
    collect: (ctx) =>
      rainAhead(ctx, 2) >= 1 ? [] : plantsWhere(ctx, 'AIL_OIGNONS_ARRACHER', (p) => isSpecies(p, [...SPECIES.garlic, ...SPECIES.onion]) && p.stage === StepStage.HARVEST),
  },
  {
    id: 'CHOUX_BRUXELLES', family: 'recolte', priority: 4, tone: 'harvest', icon: 'basket', group: true,
    months: [10, 11, 12, 1, 2], like: 'RECOLTE_PRETE', overrides: ['RECOLTE_PRETE'], defaultReason: true,
    button: { key: 'picked', action: 'PICKED' },
    collect: (ctx) => plantsWhere(ctx, 'CHOUX_BRUXELLES', (p) => isSpecies(p, SPECIES.brussels) && p.stage === StepStage.HARVEST),
  },
  {
    // Le gel approche : la saison des cultures gélives se termine.
    id: 'DERNIERES_RECOLTES', family: 'recolte', priority: 4, tone: 'harvest', icon: 'basket', group: true,
    months: [9, 10], overrides: ['RECOLTE_PRETE'], defaultReason: true,
    button: { key: 'picked', action: 'PICKED' },
    collect: (ctx) => plantsWhere(ctx, 'DERNIERES_RECOLTES', (p) => p.frostSensitive && outdoor(p) && p.stage === StepStage.HARVEST),
  },

  // La croissance et l'entretien.
  {
    id: 'LEVEE_ATTENDUE', family: 'croissance', priority: 5, tone: 'go', icon: 'sprout', group: true, estimated: true,
    months: [3, 4, 5, 6, 7, 8, 9], defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) =>
      gardenPlants(ctx)
        .filter((p) => p.stage === StepStage.SOWING_IN_PLACE && p.nextStepStage === StepStage.EMERGENCE && p.estimatedDaysLeft != null && p.estimatedDaysLeft <= 0)
        .map((p) => ({ code: 'LEVEE_ATTENDUE', plants: [gardenPlant(ctx, p)], reason: where(p), params: { n: daysSince(ctx, p.plantedDate) ?? 0 }, estimated: true })),
  },
  {
    id: 'PREMIERES_FLEURS', family: 'croissance', priority: 6, tone: 'go', icon: 'sprout', group: true, estimated: true,
    months: [4, 5, 6, 7, 8], defaultReason: true,
    collect: (ctx) =>
      plantsWhere(ctx, 'PREMIERES_FLEURS', (p) => p.nextStepStage === StepStage.FLOWERING && p.estimatedDaysLeft != null && p.estimatedDaysLeft <= 1, { estimated: true }),
  },
  {
    id: 'LIMACES_APRES_PLUIE', family: 'maladie', priority: 3, tone: 'go', icon: 'droplet', weather: true, group: true,
    months: [3, 4, 5, 6, 7, 8, 9, 10], defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) => {
      const rain = ctx.advice?.lastRain;
      const mild = (tonight(ctx)?.tempMin ?? -Infinity) >= 8;
      if (!rain || rain.precipitation < 5 || dryDays(ctx) > 1 || !mild) return [];
      return plantsWhere(ctx, 'LIMACES_APRES_PLUIE', (p) => (p.isYoung || isSpecies(p, SPECIES.lettuce)) && outdoor(p) && !resting(p));
    },
  },
  {
    id: 'ECLAIRCIR_CAROTTES', family: 'entretien', priority: 5, tone: 'go', icon: 'sprout', group: true, estimated: true,
    months: [4, 5, 6, 7], like: 'ENTRETIEN', defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) =>
      plantsWhere(ctx, 'ECLAIRCIR_CAROTTES', (p) => isSpecies(p, SPECIES.carrot) && (p.stage === StepStage.EMERGENCE || (p.stage === StepStage.GROWTH && p.isYoung))),
  },
  {
    id: 'BUTTER_POMMES_DE_TERRE', family: 'entretien', priority: 5, tone: 'go', icon: 'sprout', group: true, estimated: true,
    months: [5, 6], like: 'ENTRETIEN', defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) => plantsWhere(ctx, 'BUTTER_POMMES_DE_TERRE', (p) => isSpecies(p, SPECIES.potato) && p.stage === StepStage.GROWTH),
  },
  {
    id: 'PINCER_FEVES', family: 'entretien', priority: 5, tone: 'go', icon: 'sprout', group: true,
    months: [5, 6], like: 'ENTRETIEN', defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) => plantsWhere(ctx, 'PINCER_FEVES', (p) => isSpecies(p, SPECIES.broadBean) && p.stage === StepStage.FLOWERING),
  },
  {
    id: 'BUTTER_POIREAUX', family: 'entretien', priority: 5, tone: 'go', icon: 'sprout', group: true,
    months: [9, 10], like: 'ENTRETIEN', defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) =>
      plantsWhere(ctx, 'BUTTER_POIREAUX', (p) => isSpecies(p, SPECIES.leek) && outdoor(p) && p.stage === StepStage.GROWTH && (daysSince(ctx, p.plantedDate) ?? 0) >= 42),
  },
  {
    id: 'TAILLER_VIGNE', family: 'entretien', priority: 5, tone: 'go', icon: 'leaf', number: 'singular', weather: true,
    months: [12, 1, 2], like: 'ENTRETIEN', defaultReason: true,
    button: { key: 'done', action: 'DONE' },
    collect: (ctx) =>
      forecast(ctx).slice(0, 2).every((d) => (d.tempMin ?? Infinity) > 0)
        ? plantsWhere(ctx, 'TAILLER_VIGNE', (p) => isSpecies(p, SPECIES.vine) && resting(p)).slice(0, 1)
        : [],
  },
  {
    id: 'RHUBARBE_SE_REFAIT', family: 'croissance', priority: 6, tone: 'calm', icon: 'leaf', number: 'singular',
    months: [7], like: 'RIEN_DE_SPECIAL', defaultReason: true,
    collect: (ctx) => plantsWhere(ctx, 'RHUBARBE_SE_REFAIT', (p) => isSpecies(p, SPECIES.rhubarb) && !resting(p)).slice(0, 1),
  },

  // Les jours calmes : ils partent d'une plante, d'un sachet ou d'une zone du jardin.
  {
    // « Une plante qui passe l'hiver » : poireaux, choux, mâche dehors.
    id: 'HIVER_DEHORS', family: 'calme', priority: 7, tone: 'calm', icon: 'leaf', weather: true, group: true, groupBy: 'cultivation',
    months: WINTER, defaultReason: true,
    button: { key: 'picked', action: 'PICKED' },
    collect: (ctx) => {
      const t = frostTonight(ctx);
      const days = forecast(ctx);
      const variant: Omit<DayCandidate, 'code' | 'plants'> =
        t != null
          ? { signalVariant: 'frost', params: { t }, announces: 'FROST' }
          : { signalVariant: 'calm', params: { day: weekday(ctx, days[days.length - 1]?.date ?? localDay(ctx.now)) } };
      return plantsWhere(ctx, 'HIVER_DEHORS', (p) => isSpecies(p, HARDY) && outdoor(p) && [StepStage.GROWTH, StepStage.HARVEST].includes(p.stage), variant);
    },
  },
  {
    // « Une récolte qui attend » : le gel de cette nuit rend le chou frisé plus doux.
    id: 'MEILLEUR_APRES_GEL', family: 'calme', priority: 7, tone: 'calm', icon: 'snowflake', weather: true, announces: 'FROST', number: 'singular',
    months: WINTER, defaultReason: true,
    button: { key: 'picked', action: 'PICKED' },
    collect: (ctx) => {
      const night = tonight(ctx);
      if (night?.tempMin == null || night.tempMin > 0) return [];
      return plantsWhere(ctx, 'MEILLEUR_APRES_GEL', (p) => isSpecies(p, SWEETER_AFTER_FROST) && outdoor(p) && p.stage === StepStage.HARVEST, {
        params: { t: Math.round(night.tempMin) },
      }).slice(0, 1);
    },
  },
  {
    // « Des graines pour la suite » : les sachets qui attendent le printemps, et le premier à partir.
    id: 'GRAINES_POUR_LA_SUITE', family: 'calme', priority: 7, tone: 'calm', icon: 'sprout', counted: true,
    months: [10, 11, 12, 1],
    button: { key: 'prepare-season', action: 'OPEN' },
    collect: (ctx): DayCandidate[] => {
      const waiting = springSeeds(ctx);
      if (!waiting.length) return [];
      const all = plantWords(waiting.map((w) => seedPlant(w.seed)), ctx.lang);
      const first = plantWords([seedPlant(waiting[0].seed)], ctx.lang);
      const month = formatVwDate(waiting[0].opens, 'month-name', ctx.lang);
      return [{
        code: 'GRAINES_POUR_LA_SUITE', plants: [], params: { n: waiting.length }, link: routesConstants.GARDEN_SEEDS,
        reason: [
          { key: 'home.day.reason.names', params: { names: all.params['bare'] ?? all.params['names'] } },
          first.agreed
            ? { key: 'home.day.msg.GRAINES_POUR_LA_SUITE.first', params: { plantsSubject: first.params['plantsSubject'], month }, form: first.form }
            : { key: 'home.day.msg.GRAINES_POUR_LA_SUITE.first-plain', params: { names: first.params['names'], month } },
        ],
      }];
    },
  },
  {
    // « Une plante qui avance » : la récolte est encore loin, d'après les degrés-jours.
    id: 'PREND_SON_TEMPS', family: 'calme', priority: 7, tone: 'calm', icon: 'sprout', number: 'singular', estimated: true,
    months: WINTER, defaultReason: true,
    button: { key: 'update-plant', action: 'OPEN' },
    collect: (ctx) =>
      gardenPlants(ctx)
        .filter((p) => p.stage === StepStage.GROWTH && p.nextStepStage === StepStage.HARVEST && p.estimatedDaysLeft != null && p.estimatedDaysLeft >= 7)
        .slice(0, 1)
        .map((p) => {
          const ready = new Date(ctx.now.getFullYear(), ctx.now.getMonth(), ctx.now.getDate() + p.estimatedDaysLeft!);
          const place = p.effectiveLocationType === LocationType.GREENHOUSE ? 'greenhouse' : p.effectiveLocationType === LocationType.INDOOR ? 'indoor' : 'garden';
          return {
            code: 'PREND_SON_TEMPS', plants: [gardenPlant(ctx, p)], reason: where(p), signalVariant: place, estimated: true,
            params: { date: formatVwDate(ready, 'day-month', ctx.lang) },
            link: p.plantUid ? `/garden/plants/${p.plantUid}?action=choose-step` : null,
          };
        }),
  },
  {
    id: 'ATTENDRE_EN_TERRE', family: 'calme', priority: 7, tone: 'calm', icon: 'leaf', group: true,
    months: [9, 10, 11, 12, 1, 2], like: 'RIEN_DE_SPECIAL', defaultReason: true,
    button: { key: 'picked', action: 'PICKED' },
    collect: (ctx) => plantsWhere(ctx, 'ATTENDRE_EN_TERRE', (p) => isSpecies(p, KEEPS_IN_GROUND) && outdoor(p) && p.stage === StepStage.HARVEST),
  },
  {
    id: 'AIL_RACINES', family: 'calme', priority: 7, tone: 'calm', icon: 'leaf', number: 'singular',
    months: [10, 11, 12, 1], like: 'RIEN_DE_SPECIAL', defaultReason: true,
    collect: (ctx) =>
      plantsWhere(ctx, 'AIL_RACINES', (p) => {
        const age = daysSince(ctx, p.plantedDate);
        return isSpecies(p, SPECIES.garlic) && age != null && age >= 14 && age <= 70;
      }, { estimated: false }).slice(0, 1),
  },
  {
    id: 'FEVES_HIVER', family: 'calme', priority: 7, tone: 'calm', icon: 'leaf', group: true,
    months: WINTER, like: 'RIEN_DE_SPECIAL', defaultReason: true,
    collect: (ctx) =>
      plantsWhere(ctx, 'FEVES_HIVER', (p) => isSpecies(p, SPECIES.broadBean) && outdoor(p) && !!p.plantedDate && new Date(p.plantedDate).getMonth() >= 8, {
        estimated: false,
      }),
  },
  {
    id: 'FRAISIERS_FROID', family: 'calme', priority: 7, tone: 'calm', icon: 'snowflake', group: true,
    months: WINTER, like: 'RIEN_DE_SPECIAL', defaultReason: true,
    collect: (ctx) => plantsWhere(ctx, 'FRAISIERS_FROID', (p) => isSpecies(p, SPECIES.strawberry) && resting(p)),
  },
  {
    id: 'RHUBARBE_DORT', family: 'calme', priority: 7, tone: 'calm', icon: 'leaf', number: 'singular',
    months: WINTER, like: 'RIEN_DE_SPECIAL', defaultReason: true,
    collect: (ctx) => plantsWhere(ctx, 'RHUBARBE_DORT', (p) => isSpecies(p, SPECIES.rhubarb) && resting(p)).slice(0, 1),
  },
  {
    // Les autres vivaces au repos (asperge, artichaut, menthe…) : la rhubarbe, les fraisiers et la vigne ont leur message.
    id: 'VIVACES_REPOS', family: 'calme', priority: 7, tone: 'calm', icon: 'leaf', group: true,
    months: WINTER, like: 'RIEN_DE_SPECIAL', defaultReason: true,
    collect: (ctx) =>
      plantsWhere(ctx, 'VIVACES_REPOS', (p) => resting(p) && !isSpecies(p, [...SPECIES.rhubarb, ...SPECIES.strawberry, ...SPECIES.vine])),
  },
  {
    id: 'FLEURS_MALES_COURGES', family: 'calme', priority: 7, tone: 'calm', icon: 'sprout', group: true, estimated: true,
    months: [6, 7], like: 'RIEN_DE_SPECIAL', defaultReason: true,
    collect: (ctx) => plantsWhere(ctx, 'FLEURS_MALES_COURGES', (p) => isSpecies(p, SPECIES.squash) && p.stage === StepStage.FLOWERING),
  },
  {
    id: 'RIEN_A_CRAINDRE', family: 'calme', priority: 7, tone: 'calm', icon: 'leaf', weather: true, counted: true, defaultReason: true, wholeGarden: true,
    collect: (ctx) => {
      const days = forecast(ctx);
      const calm = days.every((d) => (d.tempMin ?? Infinity) > FROST_C && (d.windSpeed ?? 0) < WIND_KMH);
      const n = gardenPlants(ctx).length;
      return calm && n && days.length >= 2 ? [{ code: 'RIEN_A_CRAINDRE', plants: [], params: { n, day: weekday(ctx, days[days.length - 1].date) } }] : [];
    },
  },
  {
    id: 'PLUIE_A_ARROSE', family: 'calme', priority: 7, tone: 'water', icon: 'droplet',
    months: [4, 5, 6, 7, 8, 9], defaultReason: true,
    collect: (ctx) => {
      const rain = ctx.advice?.lastRain;
      if (!rain || rain.precipitation < 10 || dryDays(ctx) > 1 || !gardenPlants(ctx).some(outdoor)) return [];
      return [{ code: 'PLUIE_A_ARROSE', plants: [], params: { mm: Math.round(rain.precipitation) } }];
    },
  },
  {
    id: 'GRAINES_COURTE_VIE', family: 'hors_saison', priority: 7, tone: 'calm', icon: 'sprout', group: true,
    months: WINTER, like: 'GRAINES_SEULES', defaultReason: true,
    collect: (ctx) =>
      seeds(ctx)
        .filter((s) => isSpecies(s, SHORT_LIVED_SEEDS) && !!s.boughtDate && new Date(s.boughtDate).getFullYear() < ctx.now.getFullYear())
        .map((s) => ({ code: 'GRAINES_COURTE_VIE', plants: [seedPlant(s)] })),
  },
  {
    id: 'PREPARER_SAISON', family: 'hors_saison', priority: 7, tone: 'calm', icon: 'leaf', counted: true,
    months: WINTER, like: 'POTAGER_VIDE', defaultReason: true,
    button: { key: 'prepare-season', action: 'OPEN' },
    collect: (ctx) => {
      const zone = freeZone(ctx);
      const n = seeds(ctx).length;
      return zone && n ? [{ code: 'PREPARER_SAISON', plants: [], params: { zone: zone.name, n }, link: routesConstants.SEASON_PLAN }] : [];
    },
  },
];
